"use client";

import { PushToDataLayer } from "@asosteam/asos-web-event-tracking-bridge";
import Script from "next/script";
import {
  useEffect,
  useContext,
  createContext,
  useState,
  useCallback,
} from "react";
import { usePromise } from "@repo/ui/use-promise";
import { getApiUrl } from "@repo/utils/get-api-url";
import { handleOneTrustLoad } from "@/components/consent";
import {
  ADOBE_ANALYTICS_SCRIPT_URL,
  MIXPANEL_RECORD_SESSIONS_PERCENT,
} from "@/constants";

type AnalyticsContext = {
  pushToDataLayer: PushToDataLayer;
  resetDataLayer: () => Promise<void>;
};

const defaultAnalyticsContextValue = {
  pushToDataLayer: async () => null,
  resetDataLayer: async () => {},
} as AnalyticsContext;

export const AnalyticsContext = createContext(defaultAnalyticsContextValue);
export const useAnalytics = () => useContext(AnalyticsContext);

const logger = (error: Error) => {
  if (console) {
    console.error(error);
  }
};

const createWaitingSink = () => {
  let resolveWaitingSink: () => void = () => {};
  const waitingSink = new Promise<void>((resolve) => {
    resolveWaitingSink = resolve;
  });

  return { waitingSink, resolveWaitingSink };
};

// create fake source for now just to kick off the bridging (will refactor when tracking bridge is updated)
const createFakeSource = () => {
  const { port1: publicEventTarget } = new MessageChannel();
  return Promise.resolve(publicEventTarget);
};

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const fakeSource = createFakeSource();
  const { waitingSink, resolveWaitingSink } = createWaitingSink();
  const { promise, resolve } = usePromise<AnalyticsContext>();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) {
      return;
    }

    import("@asosteam/asos-web-event-tracking-bridge").then(
      ({ initialise, createGtmSink, createMixpanelSink }) => {
        const gtmSink = createGtmSink(
          "GTM-WSDVJP8M",
          logger,
          true,
          undefined,
          "GTM-PQSSZK68",
        );

        const MIXPANEL_PROJECT_TOKEN = "topshop";
        const targetAnalyticsApiEndpoint = getApiUrl({
          path: "/customer/signals/v1",
          searchParams: {},
        });

        const mixpanelSink = createMixpanelSink(MIXPANEL_PROJECT_TOKEN, {
          api_host: targetAnalyticsApiEndpoint,
          record_sessions_percent: MIXPANEL_RECORD_SESSIONS_PERCENT,
          record_heatmap_data: true,
          autocapture: {
            pageview: null,
            click: true,
            dead_click: true,
            rage_click: true,
            input: false,
            scroll: false,
            submit: false,
            capture_text_content: false,
          },
        });

        type ChameleonSourcePromises = {
          [key: string]: Promise<EventTarget>;
        };
        type ChameleonSinkPromises = {
          adobe: Promise<void>;
          gtm: typeof gtmSink;
          mixpanel: typeof mixpanelSink;
        };

        const initialisedAnalytics = initialise({
          sources: {
            fakeSource,
          } as ChameleonSourcePromises,
          sinks: {
            gtm: gtmSink,
            adobe: waitingSink,
            mixpanel: mixpanelSink,
          } as ChameleonSinkPromises,
          logError: logger,
        });

        handleOneTrustLoad(initialisedAnalytics.pushToDataLayer);
        resolve({
          pushToDataLayer: initialisedAnalytics.pushToDataLayer,
          resetDataLayer: initialisedAnalytics.resetDataLayer,
        });
        setIsReady(true);
      },
    );
  }, [isReady, fakeSource, waitingSink, resolve]);

  const pushToDataLayer = useCallback(
    (...args: Parameters<PushToDataLayer>) =>
      promise.then(({ pushToDataLayer }) => pushToDataLayer(...args)),
    [promise],
  );

  const resetDataLayer = useCallback(
    () => promise.then(({ resetDataLayer }) => resetDataLayer()),
    [promise],
  );

  return (
    <>
      <AnalyticsContext.Provider value={{ pushToDataLayer, resetDataLayer }}>
        {children}
      </AnalyticsContext.Provider>
      <Script
        id="adobe-web-sdk"
        src={ADOBE_ANALYTICS_SCRIPT_URL}
        type="text/javascript"
        async
        onLoad={resolveWaitingSink}
      ></Script>
    </>
  );
};
